import { userService } from './userService';

// API Configuration
const PROD_API_BASE = 'https://5z9vmw0tg6.execute-api.eu-north-1.amazonaws.com/production';
const DEV_API_BASE = 'https://9vnb05of2j.execute-api.eu-north-1.amazonaws.com/development';

// Auto-select API by environment
const API_BASE_URL = (() => {
  const fromEnv = process.env.REACT_APP_API_URL;
  if (fromEnv && fromEnv.trim().length > 0) return fromEnv;
  if (typeof window !== 'undefined') {
    const host = window.location.host || '';
    const hostname = window.location.hostname || '';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isDevSite = host.includes('saas-application-dev.s3-website.eu-north-1.amazonaws.com');
    if (isLocalhost || isDevSite) return DEV_API_BASE;
  }
  return PROD_API_BASE;
})();

export interface OutlookThread {
  conversation_id: string;
  last_message_id: string;
  last_subject: string;
  participants: string[];
  last_received_at: string;
}

export interface OutlookThreadsResponse {
  threads: OutlookThread[];
  count: number;
}

export interface SyncResult {
  success: boolean;
  conversation_id: string;
  messages_fetched: number;
  messages_inserted: number;
  attachments_processing: number;
  error?: string;
}

export interface SyncResponse {
  total_conversations: number;
  successful: number;
  failed: number;
  results: SyncResult[];
}

class OutlookThreadsService {
  /**
   * List recent Outlook threads/conversations
   */
  async listRecentThreads(): Promise<OutlookThreadsResponse> {
    try {
      const jwtToken = await userService.getAuthToken();
      
      const response = await fetch(`${API_BASE_URL}/outlook-channel-threads`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to fetch threads: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error listing recent threads:', error);
      throw error;
    }
  }

  /**
   * Sync selected conversations to the database
   */
  async syncSelectedThreads(conversationIds: string[]): Promise<SyncResponse> {
    try {
      const jwtToken = await userService.getAuthToken();
      
      const response = await fetch(`${API_BASE_URL}/outlook-channel-threads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({
          conversation_ids: conversationIds
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to sync threads: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error syncing selected threads:', error);
      throw error;
    }
  }

  /**
   * List creator threads split into assigned (to project) and unassigned
   */
  async listCreatorThreads(creatorId: string, projectUuid?: string): Promise<{ assigned: any[]; unassigned: any[]; emails: string[] }>{
    const jwtToken = await userService.getAuthToken();
    const params = new URLSearchParams();
    params.set('creator_id', creatorId);
    if (projectUuid) params.set('project_uuid', projectUuid);
    const res = await fetch(`${API_BASE_URL}/outlook/creator-threads?${params.toString()}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${jwtToken}` },
    });
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      throw new Error(`Failed to list creator threads: ${res.status} ${err}`);
    }
    return await res.json();
  }

  /** Assign selected conversation ids to a project */
  async assignThreadsToProject(projectUuid: string, conversationIds: string[]): Promise<{ updated: number; by_conversation: Record<string, number> }>{
    const jwtToken = await userService.getAuthToken();
    const res = await fetch(`${API_BASE_URL}/outlook/assign-threads-to-project`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({ project_uuid: projectUuid, conversation_ids: conversationIds }),
    });
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      throw new Error(`Failed to assign threads: ${res.status} ${err}`);
    }
    return await res.json();
  }

  /** Unassign selected conversation ids (clear project) */
  async unassignThreadsFromProject(conversationIds: string[]): Promise<{ updated: number; by_conversation: Record<string, number> }> {
    const jwtToken = await userService.getAuthToken();
    const res = await fetch(`${API_BASE_URL}/outlook/unassign-threads-from-project`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({ conversation_ids: conversationIds }),
    });
    if (!res.ok) {
      const err = await res.text().catch(() => '');
      throw new Error(`Failed to unassign threads: ${res.status} ${err}`);
    }
    return await res.json();
  }

  /**
   * Get email threads for a creator and project (excluding excluded threads)
   */
  async getEmailThreadsForCreatorProject(
    creatorId: string,
    projectUuid: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<{ threads: Array<{
      conversation_id: string;
      subject: string;
      message_count: number;
      last_received_at: string;
      first_received_at: string;
      participants: string[];
      is_excluded: boolean;
    }>; total: number }> {
    try {
      const jwtToken = await userService.getAuthToken();
      
      const params = new URLSearchParams({
        creator_id: creatorId,
        project_uuid: projectUuid,
        limit: limit.toString(),
        offset: offset.toString(),
      });
      
      const response = await fetch(`${API_BASE_URL}/outlook-threads-manager?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch threads: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.data) {
        return data.data;
      }
      throw new Error('Invalid response format');
    } catch (error) {
      console.error('Error fetching email threads:', error);
      throw error;
    }
  }

  /**
   * Exclude or include a thread from a project
   */
  async toggleThreadExclusion(
    conversationId: string,
    projectUuid: string,
    exclude: boolean,
    creatorId?: string
  ): Promise<{
      success: boolean;
      conversation_id: string;
      project_uuid: string;
      excluded: boolean;
      messages_updated: number;
      excluded_projects: string[] | null;
    }> {
    try {
      const jwtToken = await userService.getAuthToken();
      
      const response = await fetch(`${API_BASE_URL}/outlook-threads-manager`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          project_uuid: projectUuid,
          exclude: exclude,
          creator_id: creatorId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to toggle thread exclusion: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.data) {
        return data.data;
      }
      throw new Error('Invalid response format');
    } catch (error) {
      console.error('Error toggling thread exclusion:', error);
      throw error;
    }
  }
}

export const outlookThreadsService = new OutlookThreadsService(); 